import React from 'react';
import { useSelector } from 'react-redux';
import {
    yellowPendingCircle,
    roundRedX,
    greenRoundTick,
    savedRoundTick,
    greenCircle,
    navyBlueRoundTick,
    purpleRoundTick,
    purpleCircle,
    purpleLine,
    greenLine,
    navyBlueLine,
    navyBlueCircle,
    verticalGreenLine,
    greyLine,
    greyCircle,
    greenFilledCircle,
} from '../../../img/images';
import { ILocalization } from '../../../localization/definitions/types';
import { RootState } from '../../../store';

interface IProgressBarSvgLocalization {
    saved: string;
    pending: string;
    approve: string;
    approved: string;
    declined: string;
    register: string;
    registered: string;
    handIn: string;
    handedIn: string;
    evaluated: string;
    failed: string;
    withdrawn: string;
    projectInformation: string;
    proposedProject: string;
    selectSupervisor: string;
}

function withProgressBarLocalization(
    OriginalComponent: React.ComponentType<{
        localization: IProgressBarSvgLocalization;
    }>
) {
    const NewProgressBarSvgWithLocalization = () => {
        const localization = useSelector(
            (state: RootState): ILocalization => state.localization
        ).localizationStrings.susy.shared.projectProgressBar;

        return <OriginalComponent localization={localization} />;
    };
    return NewProgressBarSvgWithLocalization;
}

export const SavedSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(savedRoundTick, localization.saved, 'saved-icon');
    }
);

export const PendingSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(
            yellowPendingCircle,
            localization.pending,
            'pending-icon'
        );
    }
);

export const DeclinedSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(roundRedX, localization.declined, 'declined-icon');
    }
);

export const FailedSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(roundRedX, localization.failed, 'failed-icon');
    }
);

export const WithdrawnSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(
            roundRedX,
            localization.withdrawn,
            'withdrawn-icon'
        );
    }
);

export const AcceptedSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(
            navyBlueRoundTick,
            localization.approved,
            'accepted-icon'
        );
    }
);

export const EvaluatedSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(
            navyBlueRoundTick,
            localization.evaluated,
            'evaluated-icon'
        );
    }
);

const getCircleText = (
    circle: string,
    localization: string,
    testId: string
): JSX.Element => {
    return (
        <div data-testid={testId} className='d-flex susy-progress-bar-item'>
            <div className='circle-svg'>
                <img src={circle} alt='' />
            </div>
            <div className='project-progress-bar-text'>{localization}</div>
        </div>
    );
};

const getLineCircleText = (
    line: string,
    circle: string,
    localization: string,
    testId: string
): JSX.Element => {
    return (
        <div
            data-testid={testId}
            className='d-flex susy-progress-bar-item pl-2'
        >
            <div className='line-margin'>
                <img src={line} className='' alt='' />
            </div>
            <div className='circle-svg'>
                <img src={circle} alt='' />
            </div>
            <div className='project-progress-bar-text'>{localization}</div>
        </div>
    );
};

const getVerticalLineCircleText = (
    line: string,
    circle: string,
    localization: string,
    testId: string
): JSX.Element => {
    return (
        <div
            data-testid={testId}
            className='d-flex flex-column susy-min-width-90 justify-content-center susy-progress-bar-item vertical-progress-bar'
        >
            <div className='circle-svg'>
                <img src={circle} alt='' />
            </div>
            <div className='vertical-line'>
                <img src={line} alt='' />
            </div>
            <div className='project-progress-bar-text'>{localization}</div>
        </div>
    );
};

export const CompletedAcceptedSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            navyBlueLine,
            navyBlueRoundTick,
            localization.approved,
            'progress-bar-completed-approved'
        );
    }
);

export const NotYetReachedApprovedStateSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            navyBlueLine,
            navyBlueCircle,
            localization.approve,
            'progress-bar-not-yet-reached-approved'
        );
    }
);

export const NotYetReachedRegisterSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            greenLine,
            greenCircle,
            localization.register,
            'progress-bar-not-yet-reached-registered'
        );
    }
);

export const NotYetReachedHandInSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            purpleLine,
            purpleCircle,
            localization.handIn,
            'progress-bar-not-yet-reached-handed-in'
        );
    }
);

export const CompletedRegisteredSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            greenLine,
            greenRoundTick,
            localization.registered,
            'progress-bar-completed-registered'
        );
    }
);

export const CompletedHandedInSvg = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            purpleLine,
            purpleRoundTick,
            localization.handedIn,
            'progress-bar-completed-handed-in'
        );
    }
);

export const DateNotCompletedSvgCircle = () => {
    return (
        <div
            data-testid='date-not-completed-svg-circle'
            className='date-progress-bar vertical-circle'
        >
            {getCircleText(greenCircle, '', 'accepted-icon')}
        </div>
    );
};

export const DateNotCompletedSvgCircleWithLine = () => {
    return (
        <div
            data-testid='date-not-completed-svg-circle-with-line'
            className='date-progress-bar  vertical'
        >
            {getVerticalLineCircleText(verticalGreenLine, greenCircle, '', '')}
        </div>
    );
};

export const DateCompletedSvgCircle = () => {
    return (
        <div
            data-testid='date-completed-svg-circle'
            className='date-progress-bar vertical-circle'
        >
            {getCircleText(greenRoundTick, '', 'accepted-icon')}
        </div>
    );
};

export const DateFailedSvgCircle = () => {
    return (
        <div
            data-testid='date-failed-svg-circle'
            className='date-progress-bar vertical-circle'
        >
            {getCircleText(roundRedX, '', 'failed-icon')}
        </div>
    );
};

export const DateCompletedSvgCircleWithLine = () => {
    return (
        <div
            data-testid='date-completed-svg-circle-with-line'
            className='date-progress-bar vertical'
        >
            {getVerticalLineCircleText(
                verticalGreenLine,
                greenRoundTick,
                '',
                ''
            )}
        </div>
    );
};

export const NotYetFillProjectInformationSVGCircle =
    withProgressBarLocalization(
        ({
            localization,
        }: {
            localization: IProgressBarSvgLocalization;
        }): JSX.Element => {
            return getCircleText(
                greenCircle,
                localization.projectInformation,
                'progress-bar-not-yet-project-information'
            );
        }
    );

export const NotYetSelectSupervisorSVGCircle = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            greyLine,
            greyCircle,
            localization.selectSupervisor,
            'progress-bar-not-yet-select-supervisor'
        );
    }
);

export const FilledProjectInformationSVGCircle = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getCircleText(
            greenFilledCircle,
            localization.projectInformation,
            'progress-bar-filled-project-information'
        );
    }
);

export const SelectingSupervisorSVGCircle = withProgressBarLocalization(
    ({
        localization,
    }: {
        localization: IProgressBarSvgLocalization;
    }): JSX.Element => {
        return getLineCircleText(
            greenLine,
            greenCircle,
            localization.selectSupervisor,
            'progress-bar-not-yet-select-supervisor'
        );
    }
);
